import PropTypes from 'prop-types';
import React from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import { isDesktop } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import {
  addFavoriteContentByProfile,
  deleteFavoriteContentByProfile,
} from '../../ProfileManager/actions';

// #region /////// COMPONENTS ///////
import Footer from '../../../components/Footer';
import HStack from '../../../components/HStack';
// #endregion

import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { HandleError } from '../../../components/HandleError';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Seo from '../../../components/Seo';
import useRouter from '../../../customHooks/useRouter';
import useContentNavigation from '../../../customHooks/useContentNavigation';
import { selectContentsUrlByID } from '../ContentOverViewSection/selectors';
import { getContentsUrlById } from '../ContentOverViewSection/actions';
import { selectPathCountry } from '../../AppSectionRouter/selectors';
import { convertPath, getPathNameByContentType } from '../../../utils/pathRoutesUtils';
import { CONTENT_TYPES } from '../../../constants/contentTypes';
import { getLowestEpisode } from '../../../utils/ContentUtils';
import * as actions from './actions';
import * as hooks from './hooks';
import * as selectors from './selectors';
import './styles.scss';
import { useScript } from '../../../customHooks/useScript';
import ExtendedFooter from '../../../components/ExtendedFooter';
import useClientConfigs from '../../../customHooks/useClientConfigs';
import { getSectionInfoSeo } from '../../../utils/sectionUtils';
import { validateLiveContent } from '@tbx/experience-widgets-lib';

function HomeSection({ accessToken, section }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [showFooter, setShowFooter] = React.useState(false);
  const { t } = useTranslation();
  const {
    useExtendedFooter,
    enabledResponsiveWeb,
    CustomFooter,
    hasPayService,
    hasDirectAccessToAddOns,
  } = useClientConfigs();

  useScript(true);

  // #region /// SELECTORS ///
  const widgetCollection = useSelector((state) =>
    selectors.selectWidgetCollection(state),
  );
  const appSettings = useSelector((state) =>
    selectors.selectAppSettings(state),
  );
  const networks = useSelector((state) => selectors.selectNetworks(state));
  const channels = useSelector((state) => selectors.selectChannels(state));
  const authenticated = useSelector((state) =>
    selectors.selectAuthenticated(state),
  );
  const favorites = useSelector((state) =>
    selectors.selectFavoritesContents(state),
  );
  const widgetCollectionPaginate = useSelector((state) =>
    selectors.selectWidgetCollectionPaginate(state),
  );
  const selectIsPlayerEpgSection = useSelector((state) =>
    selectors.selectIsPlayerEpgSection(state),
  );
  const selectSectionError = useSelector((state) =>
    selectors.selectSectionError(state),
  );
  const PATHS = useSelector((state) => selectors.selectPathList(state));
  const pathCountry = useSelector((state) => selectPathCountry(state));
  const { enableContentPage, mobileAppUrls } = appSettings;

  const availableSections = useSelector((state) =>
    selectors.selectSectionCollection(state),
  );

  const redirectUrl = useSelector((state) => selectContentsUrlByID(state));
  const selectUserUrns = useSelector((state) => selectors.selectUserUrns(state));

  const showCustomMetaTags = useSelector((state) =>
    selectors.selectShowCustomMetaTags(state),
  );
  // #endregion

  const marginClassName = React.useMemo(() => {
    if (widgetCollectionPaginate.some((widget) => widget.componentType === 'banner')) {
      return '';
    }
    return 'marginTop';
  }, [widgetCollectionPaginate]);

  const { packageUpgradeMessages } = appSettings;

  const { handlePlayContent, handleSelectContent } = useContentNavigation({
    authenticated,
    accessToken,
    PATHS,
    mobileAppUrls,
    hasPayService,
    hasDirectAccessToAddOns,
    enableContentPage,
    packageUpgradeMessages,
    redirectUrl,
    enabledResponsiveWeb,
    onNavigate: () => setShowFooter(false),
    onGetContentsUrlById: (id) => dispatch(getContentsUrlById(accessToken, id)),
    onGetEpisodeBySerie: (id, hasRedirect, serieContent) => {
      const safeRouter = {
        push: (path, state) => {
          const lastSegment = path?.split('?')[0]?.split('/').pop();
          const isFallback = !lastSegment || lastSegment === 'undefined';
          if (isFallback) {
            const firstEpisode = getLowestEpisode(serieContent);
            if (firstEpisode?.id) {
              router.push(convertPath(PATHS.player, firstEpisode.id), state);
            } else {
              const seriePath = getPathNameByContentType(CONTENT_TYPES.SERIE, PATHS) || PATHS.content;
              router.push(convertPath(seriePath, id), state);
            }
          } else {
            router.push(path, state);
          }
        },
      };
      dispatch(actions.getCurrentEpisodeBySerie(id, safeRouter, PATHS.player));
    },
    selectUserUrns,
  });

  const handleOnSelectContent = async (content, options) => {
    if (
      !content ||
      !content.id ||
      content.id === 'undefined' ||
      content.id === 'null'
    )
      return false;
    handleSelectContent(content, options);
  };

  const onClickPlayerButton = (content, isBannerHero = false) => {
    if (
      !content ||
      !content.id ||
      content.id === 'undefined' ||
      content.id === 'null'
    )
      return false;
    handlePlayContent(content);
  };

  const onClickMyListButton = (contentId) => {
    favorites.some((idR) => idR === contentId)
      ? dispatch(deleteFavoriteContentByProfile(contentId))
      : dispatch(addFavoriteContentByProfile(contentId));
  };

  // #region ///// HOOK EFFECTS /////
  hooks.useDidMount(accessToken, section, authenticated);
  const { showModal, setShowModal } = hooks.usePrivacyModal(pathCountry);
  // hooks.useSectionError(selectSectionError);
  // #endregion

  const handleModalButton = (opt) => {
    if (opt === 0) {
      localStorage.setItem('usPrivacy', '1YNY');
    } else {
      localStorage.setItem('usPrivacy', '1YYY');
    }
    setShowModal(false);
  };

  const redirectHandler = (content, redirectType, target = '_self') => {
    return redirectType === 'externalUrl'
      ? window.open(content, target)
      : router.push(`${pathCountry}${content}`);
  };

  const renderFooter = () => {
    if (!showFooter || selectIsPlayerEpgSection) {
      return <></>;
    }
    if (CustomFooter) {
      return <CustomFooter mobileAppUrls={mobileAppUrls} />;
    }
    if (useExtendedFooter) {
      return <ExtendedFooter />;
    }
    return <Footer />;
  };

  if (selectSectionError?.code === 'SE-001') {
    return (
      <HandleError
        error={{
          error: t(
            'There is no home section available. Check with the system administrator.',
          ),
        }}
        navBar={false}
      />
    );
  }

  return (
    <main
      // eslint-disable-next-line react/no-unknown-property
      onLoad={() => setShowFooter(true)}
      className={`main-content homeSection ${marginClassName}`}
    >
      {!showCustomMetaTags && (
        <Seo
          title={getSectionInfoSeo(
            section,
            availableSections,
            window.location.pathname,
            'HOME',
          )}
        />
      )}
      <div className="container-wide hstack-container">
        <InfiniteScroll
          dataLength={widgetCollectionPaginate?.length}
          next={() => dispatch(actions.loadMoreWidgets())}
          hasMore={widgetCollectionPaginate?.length < widgetCollection?.length}
          loader={
            <div className="homeSection__results-loader">
              <LoadingSpinner className="homeSection__results-loader-spinner" />
            </div>
          }
        >
          <HStack
            accessToken={accessToken}
            handleOnSelectContent={handleOnSelectContent}
            onClickPlayerButton={onClickPlayerButton}
            onClickMyListButton={onClickMyListButton}
            favorites={favorites}
            authenticated={authenticated}
            sectionID={section.id}
            widgetCollection={widgetCollectionPaginate}
            appSettings={appSettings}
            networks={networks}
            channels={channels}
            redirectHandler={redirectHandler}
          />
        </InfiniteScroll>
      </div>
      {renderFooter()}
      <Modal
        backdrop="static"
        contentClassName="forgotModal__content"
        className="forgotModal__container privacy-modal"
        keyboard={false}
        show={showModal}
        onHide={() => setShowModal(false)}
      >
        <Modal.Header className="forgotModal__header">
          <Modal.Title>Antes de continuar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Label>
              ¿Está de acuerdo con compartir sus datos personales con terceros?
              Esto nos permite brindarle una experiencia más personalizada y
              relevante
            </Form.Label>
          </Form>
        </Modal.Body>
        <Modal.Footer className="forgotModal__buttons">
          <Button
            className="forgotModal__buttons__secondary"
            onClick={() => handleModalButton(0)}
          >
            Rechazar
          </Button>
          <Button
            className="forgotModal__buttons__primary"
            onClick={() => handleModalButton(1)}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

HomeSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
    isHome: PropTypes.bool,
    isPublic: PropTypes.bool,
    name: PropTypes.string.isRequired,
    redirect: PropTypes.object,
    seo: PropTypes.object,
  }),
};

export default HomeSection;
